import React, { useState, useMemo, useEffect } from 'react';
import { Users, Calendar, Search, ChevronRight, AlertCircle, X, Home, Edit2, Trash2, Printer, FileText, Image } from 'lucide-react';
import BASE_API_URL from '../../../config/baseApi';
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';

export default function RoomAllocationForm() {
  const [reservations, setReservations] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [guestFromCategories, setGuestFromCategories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [noOfPaxCategories, setNoOfPaxCategories] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [usRate, setUsRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState('10');
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRegistrationId, setEditingRegistrationId] = useState(null);

  const [allocatedRooms, setAllocatedRooms] = useState([]);

  const [formData, setFormData] = useState({
    reservationCode: '',
    reservationDate: '',
    reservationTime: '',
    daysStay: '',
    departureDate: '',
    departureTime: '',
    totalPax: '',
    guestType: '',
    guestName: '',
    contactNo: '',
    email: '',
    idPass: '',
    expireDate: '',
    address: '',
    country: '',
    guestFromCat: '',
    roomType: '',
    paxPerRoom: '',
    mealPlan: '',
    roomsNeed: '',
    currency: '',
    us: '',
    rs: '',
    adults: '',
    children: '',
    infants: '',
    profession: '',
    description: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const roomsNeed = parseInt(formData.roomsNeed) || 0;
    if (roomsNeed > 0) {
      const newAllocatedRooms = Array.from({ length: roomsNeed }, (_, index) => ({
        room_no: allocatedRooms[index]?.room_no || '',
        sequence: index + 1,
        is_primary: index === 0
      }));
      setAllocatedRooms(newAllocatedRooms);
    } else {
      setAllocatedRooms([]);
    }
  }, [formData.roomsNeed]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${BASE_API_URL}/api/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      
      setReservations(data.reservations || []);
      setRegistrations(data.registrations || []);
      setAvailableRooms(data.availableRooms || []);
      setGuestFromCategories(data.guestFromCategories || []);
      setRoomTypes(data.roomTypes || []);
      setNoOfPaxCategories(data.noOfPaxCategories || []);
      setMealPlans(data.mealPlans || []);
      setCountries(data.countries || []);
      setUsRate(parseFloat(data.usRate) || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservationDetails = async (reservationId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${BASE_API_URL}/api/registrations/fetch-reservation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reservation_id: reservationId })
      });
      if (!response.ok) throw new Error('Failed to fetch reservation');
      const data = await response.json();
      
      setFormData({
        reservationCode: data.reservation_code || '',
        reservationDate: data.reservation_date || '',
        reservationTime: data.reservation_time || '',
        daysStay: data.no_of_day || '',
        departureDate: data.departure_date || '',
        departureTime: '',
        totalPax: data.total_pax_count || '',
        guestType: data.guest_type || '',
        guestName: data.guest_name || '',
        contactNo: data.contact_no || '',
        email: data.email || '',
        idPass: '',
        expireDate: '',
        address: '',
        country: data.guest_country || '',
        guestFromCat: data.guest_from_cat || '',
        roomType: data.room_type || '',
        paxPerRoom: data.no_of_pax || '',
        mealPlan: data.meal_plan || '',
        roomsNeed: data.rooms_need || '',
        currency: 'Rs',
        us: data.us || '',
        rs: data.rs || '',
        adults: data.adults || '',
        children: data.children || '',
        infants: data.infants || '',
        profession: '',
        description: data.description || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReservationClick = async (reservation) => {
    setSelectedReservation(reservation.id);
    setIsEditMode(false);
    setEditingRegistrationId(null);
    await fetchReservationDetails(reservation.id);
  };

  const handleRoomSelection = (index, roomNo) => {
    const isDuplicate = allocatedRooms.some((room, i) => 
      i !== index && room.room_no === roomNo && roomNo !== ''
    );
    if (isDuplicate) {
      setError('Cannot allocate the same room multiple times!');
      setTimeout(() => setError(null), 3000);
      return;
    }
    const updatedRooms = [...allocatedRooms];
    updatedRooms[index] = { ...updatedRooms[index], room_no: roomNo };
    setAllocatedRooms(updatedRooms);
    setError(null);
  };

  const getAvailableRoomsForSlot = (currentIndex) => {
    const selectedRoomNos = allocatedRooms
      .map((room, index) => index !== currentIndex ? room.room_no : null)
      .filter(Boolean);
    return availableRooms.filter(room => !selectedRoomNos.includes(room.room_no));
  };

  // ─── GROUPED REGISTRATIONS ────────────────────────
  const groupedRegistrations = useMemo(() => {
    const filtered = registrations.filter(reg =>
      (reg.guest_name?.toLowerCase().includes(tableSearchTerm.toLowerCase())) ||
      (reg.reservation_code?.toLowerCase().includes(tableSearchTerm.toLowerCase())) ||
      (reg.allocated_room_no?.toString().includes(tableSearchTerm)) ||
      (reg.guest_country?.toLowerCase().includes(tableSearchTerm.toLowerCase())) ||
      (reg.contact_no?.includes(tableSearchTerm)) ||
      (reg.email?.toLowerCase().includes(tableSearchTerm.toLowerCase()))
    );

    // Group by reservation_code + key_room
    const groups = {};
    filtered.forEach(reg => {
      const key = `${reg.reservation_code}_${reg.key_room || 'null'}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(reg);
    });

    // Sort each group by id ascending
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => a.id - b.id);
    });

    return Object.values(groups);
  }, [registrations, tableSearchTerm]);

  // ─── PAGINATION ────────────────────────────────────
  const entriesPerPage = parseInt(showEntries);
  const totalPages = Math.ceil(groupedRegistrations.length / entriesPerPage);
  const paginatedGroups = groupedRegistrations.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // ─── EDIT ──────────────────────────────────────────
  const handleEditRegistration = async (registration) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${BASE_API_URL}/api/registrations/${registration.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch registration');
      const data = await response.json();

      setIsEditMode(true);
      setEditingRegistrationId(data.id);
      setSelectedReservation(null);

      setFormData({
        reservationCode: data.reservation_code || '',
        reservationDate: data.reservation_date || '',
        reservationTime: data.reservation_time || '',
        daysStay: data.no_of_day || '',
        departureDate: data.departure_date || '',
        departureTime: data.departure_time || '',
        totalPax: data.total_pax_count || '',
        guestType: data.guest_type || '',
        guestName: data.guest_name || '',
        contactNo: data.contact_no || '',
        email: data.email || '',
        idPass: data.id_pass || '',
        expireDate: data.expire_date || '',
        address: data.address || '',
        country: data.guest_country || '',
        guestFromCat: data.guest_from_cat || '',
        roomType: data.room_type || '',
        paxPerRoom: data.no_of_pax || '',
        mealPlan: data.meal_plan || '',
        roomsNeed: data.rooms_need || '',
        currency: data.currency || 'Rs',
        us: data.us || '',
        rs: data.rs || '',
        adults: data.adults || '',
        children: data.children || '',
        infants: data.infants || '',
        profession: data.profession || '',
        description: data.description || ''
      });

      // Rebuild allocatedRooms from sibling registrations sharing the same key_room
      const siblings = registrations.filter(
        r => r.reservation_code === data.reservation_code && r.key_room === data.key_room
      ).sort((a, b) => a.id - b.id);

      const rooms = siblings.map((s, i) => ({
        room_no: s.allocated_room_no || '',
        sequence: i + 1,
        is_primary: i === 0
      }));
      setAllocatedRooms(rooms);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── DELETE ─────────────────────────────────────────
  const handleDeleteRegistration = async (id) => {
    if (!window.confirm('This will delete all linked room registrations under this group. Proceed?')) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${BASE_API_URL}/api/registrations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to delete');
      }
      alert('Registration(s) deleted successfully');
      await fetchInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── PRINT ──────────────────────────────────────────
  const handlePrintRegistration = async (id) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${BASE_API_URL}/api/registrations/${id}/print`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch print data');
      const data = await response.json();
      // TODO: Open print modal/window with data
      console.log('Print data:', data);
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── SUBMIT (create or update) ─────────────────────
  const handleSubmit = async () => {
    try {
      setError(null);

      if (!formData.guestName) { setError('Please enter guest name'); return; }
      if (!formData.reservationDate) { setError('Please enter reservation date'); return; }
      if (!formData.departureDate) { setError('Please enter departure date'); return; }
      if (!formData.country) { setError('Please select guest country'); return; }
      if (!formData.roomType) { setError('Please select room type'); return; }
      if (!formData.roomsNeed || formData.roomsNeed < 1) { setError('Please enter number of rooms needed'); return; }
      if (allocatedRooms.length > 0 && !allocatedRooms[0].room_no) { setError('Please allocate the primary room (Room 1)'); return; }

      const reservationCode = formData.reservationCode || ('WALK-' + Date.now());
      const allocatedCount = allocatedRooms.filter(r => r.room_no).length;
      const roomsNeed = parseInt(formData.roomsNeed);

      if (allocatedCount > 0 && allocatedCount < roomsNeed) {
        if (!window.confirm(`Only ${allocatedCount}/${roomsNeed} rooms allocated. Proceed?`)) return;
      }

      const payload = {
        reservation_code: reservationCode,
        allocated_rooms: JSON.stringify(allocatedRooms),
        reservation_date: formData.reservationDate,
        reservation_time: formData.reservationTime || '12:00',
        no_of_day: formData.daysStay || 1,
        departure_date: formData.departureDate,
        departure_time: formData.departureTime || '10:00',
        total_pax_count: formData.totalPax || 1,
        guest_type: formData.guestType || 'Mr.',
        guest_name: formData.guestName,
        contact_no: formData.contactNo,
        email: formData.email,
        id_pass: formData.idPass,
        expire_date: formData.expireDate || null,
        address: formData.address,
        guest_country: formData.country,
        guest_from_cat: formData.guestFromCat,
        room_type: formData.roomType,
        no_of_pax: formData.paxPerRoom,
        meal_plan: formData.mealPlan,
        rooms_need: formData.roomsNeed,
        currency: formData.currency || 'Rs',
        us: formData.us || 0,
        rs: formData.rs || 0,
        adults: formData.adults || 0,
        children: formData.children || 0,
        infants: formData.infants || 0,
        profession: formData.profession,
        description: formData.description
      };

      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      let response;
      if (isEditMode && editingRegistrationId) {
        // UPDATE
        response = await fetch(`${BASE_API_URL}/api/registrations/${editingRegistrationId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // CREATE
        response = await fetch(`${BASE_API_URL}/api/registrations`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Request failed');
      }

      const result = await response.json();
      alert(result.message || (isEditMode ? 'Updated successfully' : 'Created successfully'));
      handleReset();
      await fetchInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleReset = () => {
    setFormData({
      reservationCode: '', reservationDate: '', reservationTime: '',
      daysStay: '', departureDate: '', departureTime: '', totalPax: '',
      guestType: '', guestName: '', contactNo: '', email: '',
      idPass: '', expireDate: '', address: '', country: '',
      guestFromCat: '', roomType: '', paxPerRoom: '', mealPlan: '',
      roomsNeed: '', currency: '', us: '', rs: '',
      adults: '', children: '', infants: '', profession: '', description: ''
    });
    setAllocatedRooms([]);
    setSelectedReservation(null);
    setIsEditMode(false);
    setEditingRegistrationId(null);
    setError(null);
  };

  const filteredReservations = useMemo(() => {
    return reservations.filter(res =>
      (res.guest_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (res.reservation_code?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, reservations]);

  // ─── ROW COUNTER (for #) ───────────────────────────
  let rowCounter = (currentPage - 1) * entriesPerPage;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Allocate Rooms</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Select a reservation and allocate rooms</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-lg">
            <div className="text-xs text-gray-500 dark:text-gray-400">USD Exchange Rate</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">${usRate.toFixed(2)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* ── Reservations Sidebar ── */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden sticky top-4">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-white" />
                    <h2 className="text-lg font-bold text-white">Reservations</h2>
                  </div>
                  <span className="bg-white/20 px-2.5 py-1 rounded-full text-xs font-semibold text-white">{reservations.length}</span>
                </div>
              </div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search reservations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[600px]">
                {filteredReservations.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">No reservations found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredReservations.map((reservation) => (
                      <div
                        key={reservation.id}
                        onClick={() => handleReservationClick(reservation)}
                        className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selectedReservation === reservation.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">{reservation.reservation_code}</span>
                              {selectedReservation === reservation.id && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Selected</span>
                              )}
                            </div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">{reservation.guest_name}</h3>
                            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 space-x-2">
                              <Calendar className="w-3 h-3" />
                              <span>{reservation.reservation_date}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{reservation.rooms_need} room(s) needed</div>
                          </div>
                          <ChevronRight className={`w-5 h-5 flex-shrink-0 ${selectedReservation === reservation.id ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Click on a reservation to load details</p>
              </div>
            </div>
          </div>

          {/* ── Form Section ── */}
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="space-y-6">

              {/* Room Allocation Cards */}
              {allocatedRooms.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">1</span>
                    Room Allocation ({allocatedRooms.filter(r => r.room_no).length}/{allocatedRooms.length} allocated)
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allocatedRooms.map((roomAllocation, index) => {
                      const availableRoomsForSlot = getAvailableRoomsForSlot(index);
                      return (
                        <div key={index} className={`border-2 rounded-lg p-4 transition-all ${roomAllocation.is_primary ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Home className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Room {index + 1}</h3>
                            </div>
                            {roomAllocation.is_primary && <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-semibold rounded">Primary/Payee</span>}
                          </div>
                          <select
                            value={roomAllocation.room_no}
                            onChange={(e) => handleRoomSelection(index, e.target.value)}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required={roomAllocation.is_primary}
                          >
                            <option value="">-- Select Room --</option>
                            {availableRoomsForSlot.map(room => (
                              <option key={room.room_no} value={room.room_no}>{room.room_no} - {room.room_type}</option>
                            ))}
                            {roomAllocation.room_no && !availableRoomsForSlot.find(r => r.room_no === roomAllocation.room_no) && (
                              <option value={roomAllocation.room_no}>{roomAllocation.room_no} (Currently Selected)</option>
                            )}
                          </select>
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {roomAllocation.is_primary ? 'Primary contact and payer' : 'Additional room'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reservation Details */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">2</span>
                  Reservation Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InputField label="Check-in Date" id="reservationDate" type="date" value={formData.reservationDate} onChange={handleChange('reservationDate')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="Check-in Time" id="reservationTime" type="time" value={formData.reservationTime} onChange={handleChange('reservationTime')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="No. of Days" id="daysStay" type="number" value={formData.daysStay} onChange={handleChange('daysStay')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="Check-out Date" id="departureDate" type="date" value={formData.departureDate} onChange={handleChange('departureDate')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="Check-out Time" id="departureTime" type="time" value={formData.departureTime} onChange={handleChange('departureTime')} />
                  <InputField label="Total Guests" id="totalPax" type="number" value={formData.totalPax} onChange={handleChange('totalPax')} disabled={!!selectedReservation && !isEditMode} />
                </div>
              </div>

              {/* Guest Information */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">3</span>
                  Guest Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField label="Title" id="guestType" value={formData.guestType} onChange={handleChange('guestType')} options={[{ value: 'Mr.', label: 'Mr.' }, { value: 'Mrs.', label: 'Mrs.' }, { value: 'Ms.', label: 'Ms.' }, { value: 'Dr.', label: 'Dr.' }]} />
                  <InputField label="Guest Name" id="guestName" value={formData.guestName} onChange={handleChange('guestName')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="Contact Number" id="contactNo" value={formData.contactNo} onChange={handleChange('contactNo')} />
                  <InputField label="Email Address" id="email" type="email" value={formData.email} onChange={handleChange('email')} />
                  <SelectField label="Guest From" id="guestFromCat" options={guestFromCategories.map(item => ({ value: item.cat_name, label: item.cat_name }))} value={formData.guestFromCat} onChange={handleChange('guestFromCat')} />
                  <SelectField label="Country" id="country" options={countries.map(item => ({ value: item.country_name, label: item.country_name }))} value={formData.country} onChange={handleChange('country')} />
                  <InputField label="ID / Passport" id="idPass" value={formData.idPass} onChange={handleChange('idPass')} />
                  <InputField label="Expire Date" id="expireDate" type="date" value={formData.expireDate} onChange={handleChange('expireDate')} />
                  <InputField label="Profession" id="profession" value={formData.profession} onChange={handleChange('profession')} />
                  <div className="md:col-span-2">
                    <InputField label="Address" id="address" value={formData.address} onChange={handleChange('address')} />
                  </div>
                </div>
              </div>

              {/* Room & Meal Preferences */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">4</span>
                  Room & Meal Preferences
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <SelectField label="Room Type" id="roomType" options={roomTypes.map(item => ({ value: item.cat_name, label: item.cat_name }))} value={formData.roomType} onChange={handleChange('roomType')} />
                  <SelectField label="Guests Per Room" id="paxPerRoom" options={noOfPaxCategories.map(item => ({ value: item.cat_name, label: item.cat_name }))} value={formData.paxPerRoom} onChange={handleChange('paxPerRoom')} />
                  <SelectField label="Meal Plan" id="mealPlan" value={formData.mealPlan} onChange={handleChange('mealPlan')} options={mealPlans.map(item => ({ value: item.cat_name, label: item.cat_name }))} />
                  <InputField label="Number of Rooms" id="roomsNeed" type="number" value={formData.roomsNeed} onChange={handleChange('roomsNeed')} disabled={!!selectedReservation && !isEditMode} />
                  <InputField label="Adults" id="adults" type="number" value={formData.adults} onChange={handleChange('adults')} />
                  <InputField label="Children" id="children" type="number" value={formData.children} onChange={handleChange('children')} />
                  <InputField label="Infants" id="infants" type="number" value={formData.infants} onChange={handleChange('infants')} />
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">5</span>
                  Pricing Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectField label="Currency" id="currency" value={formData.currency} onChange={handleChange('currency')} options={[{ value: 'Rs', label: 'Sri Lankan Rupees (Rs)' }, { value: 'USD', label: 'US Dollar (USD)' }, { value: 'EUR', label: 'Euro (EUR)' }]} />
                  <InputField label="Rate (USD)" id="us" type="number" value={formData.us} onChange={handleChange('us')} />
                  <InputField label="Rate (SL Rs.)" id="rs" type="number" value={formData.rs} onChange={handleChange('rs')} />
                </div>
              </div>

              {/* Additional Notes */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 text-sm text-white font-semibold">6</span>
                  Additional Notes
                </h2>
                <label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Special Requests or Notes</label>
                <textarea id="description" value={formData.description} onChange={handleChange('description')} rows="4" className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder="Enter any special requests..." />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">Error</h3>
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                  <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800"><X className="w-5 h-5" /></button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 pb-8">
                <button
                  onClick={handleSubmit}
                  disabled={loading || (!selectedReservation && !formData.guestName)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    isEditMode ? 'Update Registration' : 'Confirm Allocation'
                  )}
                </button>
                <button onClick={handleReset} disabled={loading} className="px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
                  Reset Form
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════ */}
        {/* ──  REGISTRATIONS TABLE (grouped)         ── */}
        {/* ════════════════════════════════════════════ */}
        <div className="mt-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">

          {/* Table toolbar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-400">Show</span>
                <select value={showEntries} onChange={(e) => { setShowEntries(e.target.value); setCurrentPage(1); }} className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-700 dark:text-gray-400">entries</span>
              </div>
              <div className="relative flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-400">Search:</span>
                <input type="text" value={tableSearchTerm} onChange={(e) => { setTableSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="Guest, room, code..." className="px-4 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Guest Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Room</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Key Room</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Room Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Meal Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Check-in</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Check-out</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Days</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">USD</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="16" className="px-6 py-12 text-center text-gray-500">
                      <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </td>
                  </tr>
                ) : paginatedGroups.length === 0 ? (
                  <tr>
                    <td colSpan="16" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No registrations found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedGroups.map((group) => {
                    const firstReg = group[0];
                    return group.map((reg, subIndex) => {
                      rowCounter++;
                      const isFirst = subIndex === 0;
                      return (
                        <tr key={reg.id} className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${isFirst && group.length > 1 ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{rowCounter}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900 dark:text-white">{reg.guest_type} {reg.guest_name}</div>
                            {reg.id_pass && <div className="text-xs text-gray-500">ID: {reg.id_pass}</div>}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {reg.allocated_room_no ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">{reg.allocated_room_no}</span>
                            ) : (
                              <span className="text-xs text-gray-400">Not allocated</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{reg.key_room || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            {reg.status ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">{reg.status}</span>
                            ) : <span className="text-xs text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{reg.guest_country || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{reg.contact_no || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{reg.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{reg.room_type || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{reg.meal_plan || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            <div>{reg.reservation_date}</div>
                            <div className="text-xs text-gray-400">{reg.reservation_time}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            <div>{reg.departure_date}</div>
                            <div className="text-xs text-gray-400">{reg.departure_time || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300">{reg.no_of_day}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">${parseFloat(reg.us || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{reg.reservation_code}</span>
                          </td>
                          {/* Actions — only render once per group using rowSpan */}
                          {isFirst && (
                            <td className="px-4 py-3 text-sm" rowSpan={group.length} style={{ verticalAlign: 'middle' }}>
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleEditRegistration(reg)} className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteRegistration(reg.id)} className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handlePrintRegistration(reg.id)} className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors" title="Print">
                                  <Printer className="w-4 h-4" />
                                </button>
                                <button onClick={() => alert('Image upload for: ' + reg.id)} className="p-1.5 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors" title="Upload Image">
                                  <Image className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {groupedRegistrations.length === 0 ? 0 : ((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, groupedRegistrations.length)} of {groupedRegistrations.length} groups
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Prev</button>
              <span className="text-sm text-gray-600 dark:text-gray-400">{currentPage} / {totalPages || 1}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}